/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useCartContext } from "@farmlands/commerce";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { merge } from "lodash";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { BackButton } from "../components/BackButton";
import { PageContainer } from "../components/layout/PageContainer";
import { Loader } from "../components/Loader";
import Modal from "../components/Modal";
import {
  EditPriceModal,
  IEditedCartItemDetails,
} from "../components/modals/EditPriceModal/EditPriceModal";
import YesNoAction from "../components/YesNoAction";
import { DigitalFarmerAccount, selectCurrentFarmerAccount } from "../features/azure-b2c/storage";
import { CartProductCard } from "../features/cart/components/CartProductCard";
import CartSummary from "../features/cart/components/CartSummary";
import { usePickUpStoreModal } from "../features/cart/components/PickUpStoreModal";
import { TFOCartMenu } from "../features/cart/components/TFOCartMenu";
import { CART_URL_PARAM_KEYS, CART_URL_PARAMS } from "../features/cart/constants";
import CartSlice, {
  selectCurrentCart,
  selectOrderStatusById,
  syncCart,
} from "../features/cart/storage/cart.slice";
import { CartItem, CartObject } from "../features/cart/storage/cart.types";
import { mapToCommerceAddToCartItem } from "../features/cart/utils/commerce-cart-mapping";
import { mergeStockWithProducts } from "../features/products/components/StockAvailability/useStockAvailabilities";
import {
  getActivePrices,
  invalidatePastProducts,
  toActivePrices,
} from "../features/products/storage";
import { fetchProductDetail } from "../features/products/utils/fetchProductDetail";
import QuoteProvider from "../features/quotes/components/QuoteModal/useQuoteModal";
import { getPromotionsByMasterProductIds } from "../features/typesense/api";
import { usePermission } from "../hooks/usePermission";
import { selectRemoteConfig } from "../services/firebase/remote-config/remoteConfig.slice";
import { useAppDispatch } from "../store/rootStore";
import { TestIds } from "../testIds";
import { generateImageUrl } from "../util/helpers";
import { error } from "../util/log";
import { useBranch } from "../util/useBranch";
import { useOnlineStatus } from "../util/useOnlineStatus";

import { CartColumnView, FlexGrow, FlexShrink } from "./CartComponents";

export const DeprecatedCart = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cart = useSelector(selectCurrentCart);
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const dispatch = useAppDispatch();
  const isOnline = useOnlineStatus();
  const branchId = useBranch();
  const { addToCart: addToCommerceCart } = useCartContext();

  const sync = () => {
    if (cart && currentAccount?.accountNumber && branchId && cart.items.length > 0) {
      dispatch(syncCart({ cart, branchId, accountNumber: currentAccount.accountNumber }));
    }
  };

  const [timer, setTimer] = React.useState<NodeJS.Timeout>();

  React.useEffect(() => {
    if (
      isOnline &&
      (!cart?.syncedAt || new Date(cart?.syncedAt).getTime() < new Date(cart?.updatedAt).getTime())
    ) {
      clearTimeout(timer);
      setTimer(
        setTimeout(() => {
          sync();
        }, 1000)
      );
    }
  }, [cart?.items, currentAccount, isOnline]);

  React.useEffect(() => {
    if (cart?.lastConfirmationId && currentAccount?.accountNumber) {
      invalidatePastProducts(queryClient, currentAccount.accountNumber);
      navigate(`/products/checkout/${cart.lastConfirmationId}`, {
        state: { deliveryMode: cart.cartDeliveryMode },
      });
    }
  }, [cart?.lastConfirmationId, currentAccount?.accountNumber, queryClient, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!currentAccount?.accountNumber || busy) {
      return;
    }

    const fetchDetailsAndAddToCart = async () => {
      setBusy(true);

      // Extract `items` and `quantities`
      const itemsParam = searchParams.get(CART_URL_PARAMS.IDS);
      const quantitiesParam = searchParams.get(CART_URL_PARAMS.QTYS);
      const colorsParam = searchParams.get(CART_URL_PARAMS.COLORS);
      const sizesParam = searchParams.get(CART_URL_PARAMS.SIZES);

      // Convert comma-separated strings to arrays
      const itemsArray = itemsParam ? itemsParam.split(",").map(Number) : [];
      const quantitiesArray = quantitiesParam ? quantitiesParam.split(",").map(Number) : [];
      const colorsArray = colorsParam ? colorsParam.split(",").map(String) : [];
      const sizesArray = sizesParam ? sizesParam.split(",").map(String) : [];

      // Validate ids and quantities to ensure they are numbers
      const isValidIds = itemsArray.every((id) => !isNaN(id) && Number.isInteger(id) && id > 0);
      const isValidQuantities = quantitiesArray.every(
        (qty) => !isNaN(qty) && Number.isInteger(qty) && qty > 0
      );

      // Early return if any validation fails
      if (!isValidIds || !isValidQuantities) {
        error("Invalid ids or quantities provided. Only positive integers are allowed.");
        return; // Exit early if invalid
      }

      // Combine `id` and `quantity` into an array of objects
      const combinedItems = itemsArray.map((id, index) => ({
        id,
        quantity: quantitiesArray[index] || 0, // fallback to 0 if no corresponding quantity
        color: colorsArray[index] || undefined,
        size: sizesArray[index] || undefined,
      }));

      const productsToAddToCart: CartItem[] = [];

      try {
        // Use `Promise.all` to fetch all product details concurrently
        const fetchedProducts = await Promise.all(
          combinedItems.map(async (item) => {
            const productDetail = await fetchProductDetail(
              item.id.toString(),
              currentAccount?.accountNumber,
              branchId,
              item.size,
              item.color
            );

            return {
              ...productDetail,
              quantity: item.quantity,
              productId: item.id,
              masterProductId: item.id,
              imageUrl: generateImageUrl(productDetail.itemNumber),
              description: productDetail.name,
              // size: item.size,
              // color: item.color,
            } as CartItem;
          })
        );

        productsToAddToCart.push(...fetchedProducts);

        // Now that `productsToAddToCart` is populated, dispatch addToCart actions
        productsToAddToCart.forEach((item) => {
          dispatch(
            CartSlice.actions.addToCart({
              accountNumber: currentAccount?.accountNumber,
              product: item,
            })
          );
          addToCommerceCart(mapToCommerceAddToCartItem(item));
        });
      } catch (err) {
        error("Error adding to cart via link:", err);
      } finally {
        if (CART_URL_PARAM_KEYS.some((key) => searchParams.has(key))) {
          const newSearchParams = new URLSearchParams(searchParams);
          CART_URL_PARAM_KEYS.forEach((key) => newSearchParams.delete(key));
          setSearchParams(newSearchParams, { replace: true });
        }

        setTimeout(() => {
          setBusy(false);
        }, 200);
      }
    };

    fetchDetailsAndAddToCart();
  }, [searchParams, currentAccount?.accountNumber]);

  const cartOrMessage = React.useMemo(() => {
    if (busy) {
      return <Loader title="Adding products to cart" />;
    }
    if (currentAccount && cart && cart.items.length > 0) {
      return <YourCart cart={cart} account={currentAccount} />;
    } else {
      return (
        <YesNoAction
          bodyProps={{ sx: { mb: 3 } }}
          display="Your Cart"
          body="Your cart is empty. You can add items to your cart from the shop page."
          optionA={{
            label: "View products",
            link: "/products",
          }}
        />
      );
    }
  }, [cart, busy]);

  return (
    <PageContainer data-testid={TestIds.pages.cart}>
      <BackButton text="Shop" onClick={() => navigate("/products")} />
      {cartOrMessage}
    </PageContainer>
  );
};

interface YourCartProps {
  cart: CartObject;
  account: DigitalFarmerAccount;
}
const YourCart = ({ cart, account }: YourCartProps) => {
  const dispatch = useAppDispatch();
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const remoteConfig = useSelector(selectRemoteConfig);
  const canEditPrice = usePermission("StaffSpecific_Pricing_PriceEdit").allowed;
  const {
    addToCart: addToCommerceCart,
    setQuantity: setCommerceQuantity,
    setEditedPrice: setCommerceEditedPrice,
    removeItem: removeCommerceItem,
    clearCart: clearCommerceCart,
  } = useCartContext();

  const [openEditPriceModal, setOpenEditPriceModal] = React.useState(false);
  const [editPriceItemIndex, setEditPriceItemIndex] = React.useState<number>();
  const currentCart = useSelector(selectCurrentCart);
  const orderStatus = useSelector(selectOrderStatusById(currentCart?.offlineCartId));
  const modal = usePickUpStoreModal();
  const isOnline = useOnlineStatus();
  const branchId = useBranch();
  const [isUpdatingQuantity, setIsUpdatingQuantity] = React.useState(false);

  useEffect(() => {
    if (orderStatus?.status === "pending" && isUpdatingQuantity) {
      setIsUpdatingQuantity(false);
    }
  }, [orderStatus, isUpdatingQuantity]);

  React.useEffect(() => {
    const updateCartItemsWithStockAvailabilities = async () => {
      const updatedCartItems = (await mergeStockWithProducts(cart.items, dispatch)) as CartItem[];
      if (updatedCartItems.length > 0) {
        dispatch(
          CartSlice.actions.setStockAvailability({
            accountNumber: account.accountNumber,
            productWithStockAvailabilities: updatedCartItems,
          })
        );
      }
    };
    updateCartItemsWithStockAvailabilities();
  }, [cart?.pickupStore]);

  // The backend cart sync does not return promotion data, so re-query Typesense
  // (the source of truth for promotions) for the items in the cart and populate
  // their promotions for the badge.
  React.useEffect(() => {
    if (!remoteConfig.traderDealsEnabled) {
      return;
    }

    const updateCartItemsWithPromotions = async () => {
      const masterProductIds = [
        ...new Set(cart.items.map((item) => item.masterProductId).filter(Boolean)),
      ];
      if (masterProductIds.length === 0) {
        return;
      }
      const promotionsByMasterId = await getPromotionsByMasterProductIds(masterProductIds);
      const promotionsByProductId = cart.items.reduce<Record<number, CartItem["promotions"]>>(
        (acc, item) => {
          acc[item.productId] = promotionsByMasterId[item.masterProductId];
          return acc;
        },
        {}
      );
      dispatch(
        CartSlice.actions.setPromotions({
          accountNumber: account.accountNumber,
          promotionsByProductId,
        })
      );
    };

    updateCartItemsWithPromotions();
  }, [cart.items, remoteConfig.traderDealsEnabled, account.accountNumber, dispatch]);

  const onChangeQuantity = (value: number, item: CartItem) => {
    if (value > 0) {
      setIsUpdatingQuantity(true);
      dispatch(
        CartSlice.actions.setQuantity({
          accountNumber: account.accountNumber,
          product: item,
          amount: value,
          isOnline: isOnline,
        })
      );
      setCommerceQuantity(item.productId, value);
    }
  };

  const onRevertEditedPrice = async (item: CartItem) => {
    if (currentAccount?.accountNumber) {
      const request = await getActivePrices(
        currentAccount.accountNumber,
        [item.productId],
        branchId
      );
      const price = toActivePrices(request?.data?.prices)[0];
      if (price) {
        dispatch(
          CartSlice.actions.removeItem({
            accountNumber: currentAccount.accountNumber,
            product: item,
          })
        );

        const newItem = {
          ...item,
          editedPriceExcGst: undefined,
          lastEditedReasonCode: undefined,
          lastEditedReasonNote: undefined,
          lineId: undefined,
        } as CartItem;

        const productToAdd = merge(newItem, price) as CartItem;

        dispatch(
          CartSlice.actions.addToCart({
            accountNumber: currentAccount.accountNumber,
            product: productToAdd,
          })
        );
        removeCommerceItem(item.productId);
        addToCommerceCart(mapToCommerceAddToCartItem(productToAdd));
      }
    }
  };

  const onApplyEditedPrice = ({
    editedPrice,
    editedPriceReasonCode,
    editedPriceReasonNote,
    editedQuantity,
    item,
  }: IEditedCartItemDetails) => {
    if (currentAccount?.accountNumber) {
      dispatch(
        CartSlice.actions.setEditedPrice({
          accountNumber: currentAccount?.accountNumber,
          product: item,
          editedPrice,
          editedQuantity,
          editedPriceReasonCode,
          editedPriceReasonNote,
        })
      );
      if (editedQuantity !== item.quantity) {
        setCommerceQuantity(item.productId, editedQuantity);
      }
      setCommerceEditedPrice(item.productId, {
        editedPriceExcGst: editedPrice ?? null,
        lastEditedReasonCode: editedPriceReasonCode ?? null,
        lastEditedReasonNote: editedPriceReasonNote ?? null,
      });
    }
  };

  const [clearWarning, setClearWarning] = React.useState(false);
  const areYouSure = () => {
    return (
      <Modal
        open={clearWarning}
        title={"Remove all items from cart?"}
        fullScreen={false}
        handleClose={() => {
          setClearWarning(false);
        }}
        footer={
          <Button
            variant="contained"
            data-testid={TestIds.feat.cart.list.clearConfirm}
            onClick={() => {
              setClearWarning(false);
              clearCart();
            }}
          >
            Clear cart
          </Button>
        }
      />
    );
  };

  const clearCart = () => {
    dispatch(
      CartSlice.actions.clearCart({
        accountNumber: account.accountNumber,
      })
    );
    clearCommerceCart();
  };

  return (
    <>
      <CartColumnView>
        <FlexGrow>
          <Stack direction="row" justifyContent={"space-between"}>
            <Typography variant="display" gutterBottom data-testid={TestIds.feat.cart.list.title}>
              Your Cart
            </Typography>

            <TFOCartMenu />
          </Stack>
          {cart.items.map((item, index) => (
            <CartProductCard
              className={orderStatus?.status === "pending" ? "shimmer-container" : ""}
              key={item.lineId ?? item.productId}
              item={{ ...item }}
              onChangeQuantity={(value) => {
                onChangeQuantity(value, item);
              }}
              handleOpenEditPriceModal={() => {
                if (isUpdatingQuantity) {
                  // Do not allow editing price while updating quantity (syncing cart)
                  return;
                }
                setEditPriceItemIndex(index);
                setOpenEditPriceModal(true);
              }}
              priceEditable={account.hasTfoAccess && remoteConfig.discountEnabled && canEditPrice}
            />
          ))}

          <Box ml="auto" mr="auto">
            <Button
              variant="underline"
              color={"greyed"}
              onClick={() => setClearWarning(true)}
              data-testid={TestIds.feat.cart.list.clear}
            >
              Clear cart
            </Button>
          </Box>
          {areYouSure()}
        </FlexGrow>
        <FlexShrink>
          <CartSummary
            handleOpenPickUpStoreModal={() => {
              modal.setOpen(true);
            }}
          />
        </FlexShrink>
      </CartColumnView>
      {account.hasTfoAccess && editPriceItemIndex !== undefined && (
        <EditPriceModal
          item={cart.items[editPriceItemIndex]}
          open={openEditPriceModal}
          setOpen={setOpenEditPriceModal}
          onApplyEditedPrice={onApplyEditedPrice}
          onRevertEditedPrice={onRevertEditedPrice}
        />
      )}
    </>
  );
};

export default () => QuoteProvider(DeprecatedCart);
